/**
 * Assigning a library to one learner — or to a whole school, whose library
 * every student who enters its code then sees.
 *
 * A checklist, not a token-pasting exercise: putting lesson #25 in front of a
 * demo account should be a tick. Two levels, because they mean different things
 * — ticking a CLASS is a standing grant that keeps including lessons added to
 * it later, while ticking a LESSON grants exactly that one and nothing else.
 *
 * The dialog submits the whole intended state rather than a diff, so un-ticking
 * removes. A "save" that silently ignores removals is the kind of bug nobody
 * reports, because it looks like they mis-clicked.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Check, X, Search } from 'lucide-react';
import api from '../../services/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface Lesson {
  shareToken: string;
  title: string | null;
  thumbnailUrl: string | null;
  subject: string | null;
  grade: string | null;
  chapter: string | null;
  courseId: string | null;
}
interface Course { id: string; title: string; slug: string; isPublished: boolean }
interface Grant { scope: 'COURSE' | 'LESSON'; ref: string }

export function LearnerAccessDialog({
  target = 'learner',
  learnerId,
  learnerLabel,
  onClose,
}: {
  /** A school has no "everything" mode: its library is always a chosen set. */
  target?: 'learner' | 'school';
  learnerId: string;
  learnerLabel: string;
  onClose: (changed: boolean) => void;
}) {
  const endpoint =
    target === 'school' ? `/admin/learn/schools/${learnerId}/entitlements` : `/admin/learners/${learnerId}/entitlements`;
  const who = target === 'school' ? 'This school' : 'This account';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [mode, setMode] = useState<'ALL' | 'ASSIGNED'>('ASSIGNED');
  const [courseSel, setCourseSel] = useState<Set<string>>(new Set());
  const [lessonSel, setLessonSel] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get(endpoint)
      .then(({ data }) => {
        if (cancelled) return;
        setCourses(data.courses ?? []);
        setLessons(data.lessons ?? []);
        setMode(data.learner?.accessMode === 'ALL' ? 'ALL' : 'ASSIGNED');
        const g: Grant[] = data.grants ?? [];
        setCourseSel(new Set(g.filter((x) => x.scope === 'COURSE').map((x) => x.ref)));
        setLessonSel(new Set(g.filter((x) => x.scope === 'LESSON').map((x) => x.ref)));
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.message || 'Could not load the catalogue'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [endpoint]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(endpoint, {
        ...(target === 'learner' ? { accessMode: mode } : {}),
        grants: [
          ...[...courseSel].map((ref) => ({ scope: 'COURSE', ref })),
          ...[...lessonSel].map((ref) => ({ scope: 'LESSON', ref })),
        ],
      });
      onClose(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save');
      setSaving(false);
    }
  }, [endpoint, target, mode, courseSel, lessonSel, onClose]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return lessons;
    return lessons.filter((l) =>
      [l.title, l.subject, l.grade, l.chapter].filter(Boolean).join(' ').toLowerCase().includes(needle),
    );
  }, [lessons, q]);

  const byCourse = useMemo(() => {
    const m = new Map<string, Lesson[]>();
    for (const l of filtered) {
      const k = l.courseId ?? '__none';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(l);
    }
    return m;
  }, [filtered]);

  const total = mode === 'ALL' ? lessons.length : new Set([
    ...lessonSel,
    ...lessons.filter((l) => l.courseId && courseSel.has(l.courseId)).map((l) => l.shareToken),
  ]).size;

  const toggle = (set: Set<string>, key: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    apply(next);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[88vh] w-full max-w-[840px] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border p-5">
          <div>
            <h2 className="text-lg font-bold">Library for {learnerLabel}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {mode === 'ALL'
                ? 'This account sees every published lesson.'
                : target === 'school'
                  ? `Every student who enters this school's code sees ${total} lesson${total === 1 ? '' : 's'}.`
                  : `This account sees ${total} lesson${total === 1 ? '' : 's'} — and nothing else.`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onClose(false)} aria-label="Close"><X className="h-4 w-4" /></Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3">
          <div className={cn('flex rounded-lg border border-border p-0.5', target === 'school' && 'hidden')}>
            {(['ASSIGNED', 'ALL'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={cn('rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}
              >
                {m === 'ASSIGNED' ? 'Only what I assign' : 'Everything'}
              </button>
            ))}
          </div>
          {mode === 'ASSIGNED' && (
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a lesson…" className="h-8 pl-8 text-sm" />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : mode === 'ALL' ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Nothing to choose — this account already sees the whole library.
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {courses.map((c) => {
                const ls = byCourse.get(c.id) ?? [];
                if (!ls.length && q.trim()) return null;
                const whole = courseSel.has(c.id);
                return (
                  <section key={c.id}>
                    <label className="flex cursor-pointer items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={whole}
                        onChange={() => toggle(courseSel, c.id, setCourseSel)}
                        className="h-4 w-4 accent-[#5B2EE5]"
                      />
                      <span className="text-sm font-bold">{c.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {whole ? 'whole class, including lessons added later' : `${ls.length} lesson${ls.length === 1 ? '' : 's'}`}
                      </span>
                    </label>

                    <div className={cn('mt-2 grid gap-1.5 pl-7', whole && 'pointer-events-none opacity-45')}>
                      {ls.map((l) => (
                        <label key={l.shareToken} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50">
                          <input
                            type="checkbox"
                            checked={whole || lessonSel.has(l.shareToken)}
                            onChange={() => toggle(lessonSel, l.shareToken, setLessonSel)}
                            className="h-3.5 w-3.5 accent-[#5B2EE5]"
                          />
                          {l.thumbnailUrl && (
                            <img src={l.thumbnailUrl} alt="" className="h-7 w-12 shrink-0 rounded object-cover" loading="lazy" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-[13px]">{l.title ?? l.shareToken}</span>
                          <span className="shrink-0 text-[11px] text-muted-foreground">{l.chapter ?? l.subject ?? ''}</span>
                        </label>
                      ))}
                      {!ls.length && <p className="text-xs text-muted-foreground">No lessons in this class yet.</p>}
                    </div>
                  </section>
                );
              })}

              {/* Lessons not in any class still need to be assignable. */}
              {(byCourse.get('__none') ?? []).length > 0 && (
                <section>
                  <p className="text-sm font-bold">Not in a class</p>
                  <div className="mt-2 grid gap-1.5 pl-7">
                    {(byCourse.get('__none') ?? []).map((l) => (
                      <label key={l.shareToken} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={lessonSel.has(l.shareToken)}
                          onChange={() => toggle(lessonSel, l.shareToken, setLessonSel)}
                          className="h-3.5 w-3.5 accent-[#5B2EE5]"
                        />
                        <span className="min-w-0 flex-1 truncate text-[13px]">{l.title ?? l.shareToken}</span>
                      </label>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {error && <p className="border-t border-border px-5 py-2 text-sm text-destructive">{error}</p>}

        <div className="flex items-center justify-between gap-3 border-t border-border p-4">
          <span className="text-xs text-muted-foreground">
            {mode === 'ASSIGNED' && total === 0 && `Assign at least one lesson or ${who.toLowerCase()} sees an empty library.`}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving || loading}>
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
              Save library
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
